import * as z from "zod/v4";
import {
  chatGptListingIdFormat,
  ebayListingIdFormat,
  googleShoppingListingIdFormat,
  redoMarketplaceListingIdFormat,
  shopifyListingIdFormat,
} from "../ids";
import {
  convertEbayListingStatusToRedoListingStatus,
  EbayListingIdWithStatusSchema,
  EbayProductStatusSchema,
} from "../marketplace/ebay-listing";
import { ListingType } from "./base-listing";
import {
  ChatGptListingIdWithStatusSchema,
  ChatGptListingStatusSchema,
  convertChatGptListingStatusToRedoListingStatus,
} from "./chatgpt-listing/chatgpt-listing";
import {
  convertGoogleShoppingListingStatusToRedoListingStatus,
  GoogleShoppingListingIdWithStatusSchema,
  GoogleShoppingListingStatusSchema,
} from "./google-shopping-listing/google-shopping-listing";
import {
  RedoListingStatus,
  RedoListingStatusSchema,
} from "./redo-listing-status";
import { RedoMarketplaceListingIdWithStatusSchema } from "./redo-marketplace-listing/redo-marketplace-listing";
import {
  convertShopifyListingStatusToRedoListingStatus,
  ShopifyListingIdWithStatusSchema,
  ShopifyProductStatusSchema,
} from "./shopify-listing/shopify-listing";

export const ListingStatusSchema = {
  [ListingType.Shopify]: ShopifyListingIdWithStatusSchema.nullable(),
  [ListingType.ChatGpt]: ChatGptListingIdWithStatusSchema.nullable(),
  [ListingType.RedoMarketplace]:
    RedoMarketplaceListingIdWithStatusSchema.nullable(),
  [ListingType.Ebay]: EbayListingIdWithStatusSchema.nullable(),
  [ListingType.GoogleShopping]:
    GoogleShoppingListingIdWithStatusSchema.nullable(),
  [ListingType.General]: z.null(),
  [ListingType.Amazon]: z.null(),
  [ListingType.Walmart]: z.null(),
  [ListingType.Perplexity]: z.null(),
};

export type ListingStatusType = {
  [key in ListingType]: (typeof ListingStatusSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export type ListingStatus = { [key in ListingType]: ListingStatusType[key] };

export const ListingsWithStatusSchema = z.object({ ...ListingStatusSchema });
export type ListingsWithStatus = z.infer<typeof ListingsWithStatusSchema>;

const BaseListingSchema = z.object({ redoStatus: RedoListingStatusSchema });
export const ListingWithRedoStatusSchema = z.discriminatedUnion("listingType", [
  BaseListingSchema.extend({
    listingType: z.literal(ListingType.Shopify),
    id: shopifyListingIdFormat.schema,
    status: ShopifyProductStatusSchema,
  }),
  BaseListingSchema.extend({
    listingType: z.literal(ListingType.ChatGpt),
    id: chatGptListingIdFormat.schema,
    status: ChatGptListingStatusSchema,
  }),
  BaseListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: googleShoppingListingIdFormat.schema,
    status: GoogleShoppingListingStatusSchema,
  }),
  BaseListingSchema.extend({
    listingType: z.literal(ListingType.RedoMarketplace),
    id: redoMarketplaceListingIdFormat.schema,
    status: z.string(),
  }),
  BaseListingSchema.extend({
    listingType: z.literal(ListingType.Ebay),
    id: ebayListingIdFormat.schema,
    status: EbayProductStatusSchema,
  }),
]);
export type ListingWithRedoStatus = z.infer<typeof ListingWithRedoStatusSchema>;

export function convertListingStatusToRedoStatus(
  listingType: ListingType,
  status: string,
): RedoListingStatus {
  switch (listingType) {
    case ListingType.Shopify:
      return convertShopifyListingStatusToRedoListingStatus(
        ShopifyProductStatusSchema.parse(status),
      );
    case ListingType.ChatGpt:
      return convertChatGptListingStatusToRedoListingStatus(
        ChatGptListingStatusSchema.parse(status),
      );
    case ListingType.GoogleShopping:
      return convertGoogleShoppingListingStatusToRedoListingStatus(
        GoogleShoppingListingStatusSchema.parse(status),
      );
    case ListingType.Ebay:
      return convertEbayListingStatusToRedoListingStatus(
        EbayProductStatusSchema.parse(status),
      );
    default:
      switch (status) {
        case "active":
          return RedoListingStatus.ACTIVE;
        case "draft":
          return RedoListingStatus.DRAFT;
        case "archived":
          return RedoListingStatus.ARCHIVED;
        case "inactive":
          return RedoListingStatus.INACTIVE;
        default:
          return RedoListingStatus.DRAFT;
      }
  }
}

const statusPriority: Record<RedoListingStatus, number> = {
  [RedoListingStatus.ACTIVE]: 3,
  [RedoListingStatus.DRAFT]: 2,
  [RedoListingStatus.ARCHIVED]: 1,
  [RedoListingStatus.INACTIVE]: 0,
};

/**
 * Any active listings will mark the product family as active.
 * Then any draft listings will mark the product family as draft.
 * Then any archived listings will mark the product family as archived.
 * No listings or none of the above will mark the product family as inactive.
 */
export function getStatusForProductFamily(
  listings: ListingWithRedoStatus[],
): RedoListingStatus {
  return listings.reduce<RedoListingStatus>(
    (acc, l) =>
      statusPriority[l.redoStatus] > statusPriority[acc] ? l.redoStatus : acc,
    RedoListingStatus.INACTIVE,
  );
}
